import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  type AlertDisplayPrefs,
  patchAlertDisplayPrefs,
  prefsEqual,
  readAlertDisplayPrefs,
  isVerificationAlert,
  showOnRibbon,
  writeAlertDisplayPrefs,
} from "../alertDisplayPrefs";
import { ALERTS_PAGE_SIZE, api, WeaponAlert } from "../api";
import { useScope } from "./ScopeContext";

type WeaponAlertsContextValue = {
  pending: WeaponAlert[];
  reviewItems: WeaponAlert[];
  activeAlerts: WeaponAlert[];
  ribbonAlert: WeaponAlert | null;
  hasAny: boolean;
  expanded: boolean;
  setExpanded: (v: boolean | ((prev: boolean) => boolean)) => void;
  enlarged: WeaponAlert | null;
  setEnlarged: (a: WeaponAlert | null) => void;
  /** Hide the ribbon for the currently-active alarm(s) until a NEW alarm (new
   *  id) appears. Does NOT dismiss/resolve the underlying alert. */
  clearRibbon: () => void;
  displayPrefs: AlertDisplayPrefs;
  setDisplayPrefs: (
    patch: Partial<Pick<AlertDisplayPrefs, "verification" | "falsePositive">>,
  ) => void;
  dismissOne: ReturnType<
    typeof useMutation<
      { status: string; alert_id: string },
      Error,
      { id: string; fromHistory: boolean }
    >
  >;
  dismissAll: ReturnType<typeof useMutation<{ status: string; count: number }, Error, void>>;
  pendingTotal: number;
  confirmedTotal: number;
  historyHasMore: boolean;
};

const WeaponAlertsContext = createContext<WeaponAlertsContextValue | null>(null);

export function useWeaponAlertsContext() {
  const ctx = useContext(WeaponAlertsContext);
  if (!ctx) throw new Error("WeaponAlertsContext missing");
  return ctx;
}

function useWeaponAlertsData() {
  const qc = useQueryClient();
  // Alerts follow the same scope as everything else: an operator watching one
  // store must not be interrupted by another store's alarm, and clearing must
  // not reach outside what they can see.
  const { scopeParams, scopeKey } = useScope();

  const panel = useQuery({
    queryKey: ["weapon-alerts", scopeKey],
    queryFn: () => api.alerts.weapon(scopeParams, { limit: ALERTS_PAGE_SIZE }),
    refetchInterval: 500,
    staleTime: 250,
  });

  const dismissOne = useMutation({
    mutationFn: ({ id, fromHistory }: { id: string; fromHistory: boolean }) =>
      api.alerts.dismissWeapon(id, fromHistory),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["weapon-alerts"] }),
    onError: (err: Error) => {
      console.error("Failed to dismiss weapon alert:", err.message);
    },
  });

  const dismissAll = useMutation({
    mutationFn: () => api.alerts.dismissAllWeapon(scopeParams),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["weapon-alerts"] }),
    onError: (err: Error) => {
      console.error("Failed to clear weapon alerts:", err.message);
    },
  });

  const pendingRaw = panel.data?.pending ?? [];
  const confirmedRaw = panel.data?.confirmed ?? [];
  const history = panel.data?.history ?? [];

  // One record per id, classified by *status*. The API's `pending` list is the
  // "under verification" *column*, and it briefly includes already-cleared
  // alarms — those must follow the false-alarm setting, not verification.
  const all = useMemo(() => {
    const byId = new Map<string, WeaponAlert>();
    for (const a of [...history, ...pendingRaw, ...confirmedRaw]) {
      const prev = byId.get(a.alert_id);
      if (!prev || a.updated_at_ms >= prev.updated_at_ms) byId.set(a.alert_id, a);
    }
    return [...byId.values()].sort((a, b) => b.updated_at_ms - a.updated_at_ms);
  }, [history, pendingRaw, confirmedRaw]);

  const pending = useMemo(() => all.filter((a) => a.status === "pending"), [all]);
  const reviewItems = useMemo(
    () => all.filter((a) => a.status === "cleared" || a.status === "confirmed"),
    [all],
  );
  const pendingTotal = panel.data?.pending_total ?? pending.length;
  const confirmedTotal = panel.data?.confirmed_total ?? confirmedRaw.length;
  const historyHasMore = panel.data?.history_has_more ?? false;
  const hasAny =
    all.length > 0 || pendingTotal > 0 || confirmedTotal > 0 || historyHasMore;

  return {
    all,
    pending,
    reviewItems,
    hasAny,
    dismissOne,
    dismissAll,
    pendingTotal,
    confirmedTotal,
    historyHasMore,
  };
}

export function WeaponAlertsProvider({ children }: { children: ReactNode }) {
  const [expanded, setExpanded] = useState(false);
  const [enlarged, setEnlarged] = useState<WeaponAlert | null>(null);
  const [displayPrefs, setDisplayPrefsState] = useState(readAlertDisplayPrefs);
  // Ribbon-only snooze: ids the operator has cleared from the ribbon. The
  // underlying alerts stay in the Alerts page; a new alarm (new id) re-shows.
  const [clearedRibbonIds, setClearedRibbonIds] = useState<Set<string>>(new Set());
  const data = useWeaponAlertsData();

  const setDisplayPrefs = useCallback(
    (patch: Partial<Pick<AlertDisplayPrefs, "verification" | "falsePositive">>) => {
      setDisplayPrefsState((prev) => {
        const next = patchAlertDisplayPrefs(prev, patch);
        writeAlertDisplayPrefs(next);
        return next;
      });
    },
    [],
  );

  // Other tabs / windows keep their own useState copy. Re-read storage whenever
  // alerts refetch (500ms) and when this tab is focused, so Off takes effect
  // even if Settings was changed in another window.
  useEffect(() => {
    const sync = () => {
      const latest = readAlertDisplayPrefs();
      setDisplayPrefsState((prev) => (prefsEqual(prev, latest) ? prev : latest));
    };
    sync();
    window.addEventListener("storage", sync);
    window.addEventListener("bf-alert-display", sync);
    window.addEventListener("focus", sync);
    document.addEventListener("visibilitychange", sync);
    return () => {
      window.removeEventListener("storage", sync);
      window.removeEventListener("bf-alert-display", sync);
      window.removeEventListener("focus", sync);
      document.removeEventListener("visibilitychange", sync);
    };
  }, []);

  const activeAlerts = useMemo(() => {
    const prefs = readAlertDisplayPrefs();
    return data.all.filter((a) => showOnRibbon(a, prefs));
  }, [data.all, displayPrefs]);

  const ribbonAlert = useMemo(() => {
    const prefs = readAlertDisplayPrefs();
    return (
      activeAlerts.find(
        (a) =>
          !clearedRibbonIds.has(a.alert_id) &&
          !(isVerificationAlert(a) && prefs.verification !== "ribbon"),
      ) ?? null
    );
  }, [activeAlerts, clearedRibbonIds, displayPrefs]);

  const clearRibbon = useCallback(() => {
    setClearedRibbonIds((prev) => {
      const next = new Set(prev);
      for (const a of activeAlerts) next.add(a.alert_id);
      return next;
    });
  }, [activeAlerts]);

  const value = useMemo(
    () => ({
      pending: data.pending,
      reviewItems: data.reviewItems,
      hasAny: data.hasAny,
      dismissOne: data.dismissOne,
      dismissAll: data.dismissAll,
      pendingTotal: data.pendingTotal,
      confirmedTotal: data.confirmedTotal,
      historyHasMore: data.historyHasMore,
      activeAlerts,
      ribbonAlert,
      clearRibbon,
      displayPrefs,
      setDisplayPrefs,
      expanded,
      setExpanded,
      enlarged,
      setEnlarged,
    }),
    [
      data.pending,
      data.reviewItems,
      data.hasAny,
      data.dismissOne,
      data.dismissAll,
      data.pendingTotal,
      data.confirmedTotal,
      data.historyHasMore,
      activeAlerts,
      ribbonAlert,
      clearRibbon,
      displayPrefs,
      setDisplayPrefs,
      expanded,
      enlarged,
    ],
  );

  return <WeaponAlertsContext.Provider value={value}>{children}</WeaponAlertsContext.Provider>;
}
